import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import mammoth from "mammoth";
import { SectionLoader } from "../../../../../components/PageLoader";

const previewCache = new Map();

export function getDownloadUrl(url) {
  return url.replace("/upload/", "/upload/fl_attachment/");
}

export function getFileExtension(url) {
  return url.split(".").pop().toLowerCase().split("?")[0];
}

function buildCompactPdfUrl(url) {
  const [base, hash] = url.split("#");
  const previewParams = "toolbar=0&navpanes=0&scrollbar=1&view=FitH";
  if (!hash) {
    return `${base}#${previewParams}`;
  }
  return `${base}#${hash}&${previewParams}`;
}

async function loadDocumentContent(documentUrl, signal) {
  if (previewCache.has(documentUrl)) {
    return previewCache.get(documentUrl);
  }

  const extension = getFileExtension(documentUrl);

  if (extension === "pdf") {
    const content = { type: "pdf", url: documentUrl };
    previewCache.set(documentUrl, content);
    return content;
  }

  if (["doc", "docx"].includes(extension)) {
    const response = await fetch(documentUrl, { signal });
    const arrayBuffer = await response.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    const content = { type: "docx", html: result.value };
    previewCache.set(documentUrl, content);
    return content;
  }

  throw new Error("Unsupported file format. Please download to view.");
}

export function DocumentPreviewContent({
  documentUrl,
  documentName,
  enabled,
  compact = false,
  showDownload = true,
}) {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !documentUrl) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const loadedContent = await loadDocumentContent(
          documentUrl,
          controller.signal,
        );
        if (!cancelled) {
          setContent(loadedContent);
        }
      } catch (err) {
        if (!cancelled && err.name !== "AbortError") {
          console.error("Error loading document:", err);
          setError(
            err.message || "Failed to load document. Please try downloading instead.",
          );
          setContent(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [documentUrl, enabled]);

  useEffect(() => {
    if (!enabled) {
      setContent(null);
      setError(null);
      setLoading(false);
    }
  }, [enabled]);

  const viewerHeight = compact ? "360px" : "70vh";
  const loaderMinHeight = compact ? "240px" : "400px";

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: loaderMinHeight }}
      >
        <SectionLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="alert alert-warning text-center"
        style={{ margin: compact ? "1rem" : "2rem" }}
      >
        <p>{error}</p>
        {showDownload && (
          <a
            href={getDownloadUrl(documentUrl)}
            download
            className="btn btn-primary mt-2"
          >
            Download Document
          </a>
        )}
      </div>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <div>
      {showDownload && (
        <div className="d-flex justify-content-end mb-3">
          <a
            href={getDownloadUrl(documentUrl)}
            download={documentName}
            className="btn btn-success text-white"
          >
            Download {documentName}
          </a>
        </div>
      )}

      {content.type === "pdf" && (
        <div
          className="pdf-viewer"
          style={{ height: viewerHeight, overflow: "hidden" }}
        >
          <iframe
            src={compact ? buildCompactPdfUrl(content.url) : content.url}
            title="PDF Viewer"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              pointerEvents: "auto",
            }}
          />
        </div>
      )}

      {content.type === "docx" && (
        <div
          className={`docx-viewer ${compact ? "docx-viewer--compact" : ""} p-4`}
          style={{
            backgroundColor: "#fff",
            minHeight: compact ? "240px" : "400px",
            maxHeight: compact ? "360px" : "none",
            overflow: compact ? "auto" : "visible",
          }}
          dangerouslySetInnerHTML={{ __html: content.html }}
        />
      )}
    </div>
  );
}

DocumentPreviewContent.propTypes = {
  documentUrl: PropTypes.string,
  documentName: PropTypes.string,
  enabled: PropTypes.bool,
  compact: PropTypes.bool,
  showDownload: PropTypes.bool,
};

DocumentPreviewContent.defaultProps = {
  documentUrl: "",
  documentName: "Resume",
  enabled: false,
  compact: false,
  showDownload: true,
};
